/**
 * VaultContext - State management for the Vault Dashboard
 *
 * Manages portfolio value, chart data, streaks, milestones, ATH,
 * celebrations queue, and scrubbing state.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import apiService, {
  VaultTimeframe,
  VaultChartDataPoint,
  VaultStreakData,
  VaultATHData,
  VaultMilestoneData,
  VaultNextMilestone,
  VaultDashboardResponse,
  VaultChartResponse,
} from '../services/api';

// Celebration types
export type CelebrationType = 'ath' | 'milestone' | 'streak';

export interface Celebration {
  id: string;
  type: CelebrationType;
  data: {
    value?: number;
    name?: string;
    count?: number;
    icon?: string;
  };
  timestamp: Date;
  dismissed: boolean;
}

// Vault state interface
export interface VaultState {
  // Portfolio data
  currentValue: number;
  previousValue: number;
  displayValue: number;
  change24h: number;
  change24hPercent: number;
  solPrice: number;

  // Historical data (by timeframe)
  chartData: Map<VaultTimeframe, VaultChartDataPoint[]>;
  selectedTimeframe: VaultTimeframe;
  chartChange: number;
  chartChangePercent: number;

  // Scrubbing state
  isScrubbing: boolean;
  scrubIndex: number | null;
  scrubValue: number | null;
  scrubTimestamp: string | null;

  // Gamification
  streaks: VaultStreakData | null;
  ath: VaultATHData | null;
  milestones: {
    achieved: VaultMilestoneData[];
    next: VaultNextMilestone | null;
    total: number;
  };

  // Celebrations
  pendingCelebrations: Celebration[];
  activeCelebration: Celebration | null;
  celebrationsEnabled: boolean;

  // UI state
  isRefreshing: boolean;
  isLoading: boolean;
  isChartLoading: boolean;
  lastUpdated: Date | null;
  error: string | null;
  isNewUser: boolean;
  unreadNotifications: number;
}

// Context type
interface VaultContextType extends VaultState {
  // Actions
  refresh: () => Promise<void>;
  setTimeframe: (timeframe: VaultTimeframe) => void;

  // Scrubbing
  startScrub: () => void;
  updateScrub: (index: number) => void;
  endScrub: () => void;

  // Celebrations
  dismissCelebration: (id: string) => void;
  triggerCelebration: (type: CelebrationType, data: Celebration['data']) => void;

  // Value animation
  animateValueTo: (value: number) => void;
}

const VaultContext = createContext<VaultContextType | undefined>(undefined);

// Timeframes to pre-load for Quick View cards
const QUICK_VIEW_TIMEFRAMES: VaultTimeframe[] = ['1D', '1W', '1M', '1Y'];

// Custom hook to use vault context
export const useVault = (): VaultContextType => {
  const context = useContext(VaultContext);
  if (!context) {
    throw new Error('useVault must be used within a VaultProvider');
  }
  return context;
};

// Initial state
const initialState: VaultState = {
  currentValue: 0,
  previousValue: 0,
  displayValue: 0,
  change24h: 0,
  change24hPercent: 0,
  solPrice: 0,
  chartData: new Map(),
  selectedTimeframe: '1M',
  chartChange: 0,
  chartChangePercent: 0,
  isScrubbing: false,
  scrubIndex: null,
  scrubValue: null,
  scrubTimestamp: null,
  streaks: null,
  ath: null,
  milestones: {
    achieved: [],
    next: null,
    total: 0,
  },
  pendingCelebrations: [],
  activeCelebration: null,
  celebrationsEnabled: true,
  isRefreshing: false,
  isLoading: true,
  isChartLoading: false,
  lastUpdated: null,
  error: null,
  isNewUser: false,
  unreadNotifications: 0,
};

interface VaultProviderProps {
  children: ReactNode;
}

export const VaultProvider: React.FC<VaultProviderProps> = ({ children }) => {
  const [state, setState] = useState<VaultState>(initialState);
  const previousATHRef = useRef<number | null>(null);
  const previousStreakRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Generate unique celebration ID
  const generateCelebrationId = useCallback(() => {
    return `celebration-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Trigger a celebration
  const triggerCelebration = useCallback((type: CelebrationType, data: Celebration['data']) => {
    const celebration: Celebration = {
      id: generateCelebrationId(),
      type,
      data,
      timestamp: new Date(),
      dismissed: false,
    };

    setState(prev => ({
      ...prev,
      pendingCelebrations: [...prev.pendingCelebrations, celebration],
    }));
  }, [generateCelebrationId]);

  // Dismiss a celebration
  const dismissCelebration = useCallback((id: string) => {
    setState(prev => {
      const updated = prev.pendingCelebrations.map(c =>
        c.id === id ? { ...c, dismissed: true } : c
      );
      const remaining = updated.filter(c => !c.dismissed);

      return {
        ...prev,
        pendingCelebrations: remaining,
        activeCelebration: remaining.length > 0 ? remaining[0] : null,
      };
    });
  }, []);

  // Check for new celebrations based on data changes
  const checkForCelebrations = useCallback((
    newATH: VaultATHData | null,
    newStreaks: VaultStreakData | null
  ) => {
    // Check for new ATH
    if (newATH && newATH.is_at_ath && previousATHRef.current !== null) {
      if (newATH.value > previousATHRef.current) {
        triggerCelebration('ath', { value: newATH.value });
      }
    }

    // Check for streak milestones (3, 5, 7, 10, etc.)
    if (newStreaks && newStreaks.current.type === 'winning') {
      const streakMilestones = [3, 5, 7, 10, 14, 21, 30];
      const currentCount = newStreaks.current.count;
      const prevCount = previousStreakRef.current || 0;

      for (const milestone of streakMilestones) {
        if (currentCount >= milestone && prevCount < milestone) {
          triggerCelebration('streak', { count: currentCount });
          break;
        }
      }
    }

    // Update refs
    if (newATH) previousATHRef.current = newATH.value;
    if (newStreaks) previousStreakRef.current = newStreaks.current.count;
  }, [triggerCelebration]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async (): Promise<VaultDashboardResponse | null> => {
    try {
      const data = await apiService.vault.getDashboard();
      return data;
    } catch (error) {
      console.error('Failed to fetch vault dashboard:', error);
      return null;
    }
  }, []);

  // Fetch chart data for a timeframe
  const fetchChart = useCallback(async (timeframe: VaultTimeframe): Promise<VaultChartResponse | null> => {
    try {
      const data = await apiService.vault.getChart(timeframe);
      return data;
    } catch (error) {
      console.error(`Failed to fetch vault chart for ${timeframe}:`, error);
      return null;
    }
  }, []);

  // Refresh all data
  const refresh = useCallback(async () => {
    setState(prev => ({ ...prev, isRefreshing: true, error: null }));

    try {
      // Fetch dashboard and all Quick View timeframes in parallel
      const [dashboardData, ...chartResults] = await Promise.all([
        fetchDashboard(),
        ...QUICK_VIEW_TIMEFRAMES.map(tf => fetchChart(tf)),
      ]);

      if (dashboardData) {
        // Check for celebrations before updating state
        checkForCelebrations(dashboardData.ath, dashboardData.streaks);

        setState(prev => {
          const newChartData = new Map(prev.chartData);

          // Populate chart data for all Quick View timeframes
          QUICK_VIEW_TIMEFRAMES.forEach((tf, index) => {
            const chartData = chartResults[index];
            if (chartData) {
              newChartData.set(tf, chartData.data);
            }
          });

          // Get chart data for selected timeframe to set change values
          const selectedChartData = chartResults[QUICK_VIEW_TIMEFRAMES.indexOf(state.selectedTimeframe)];

          return {
            ...prev,
            currentValue: dashboardData.portfolio.current_value,
            previousValue: prev.currentValue || dashboardData.portfolio.current_value,
            displayValue: dashboardData.portfolio.current_value,
            change24h: dashboardData.portfolio.change_24h,
            change24hPercent: dashboardData.portfolio.change_24h_percent,
            solPrice: dashboardData.portfolio.sol_price,
            streaks: dashboardData.streaks,
            ath: dashboardData.ath,
            milestones: {
              achieved: dashboardData.milestones.achieved,
              next: dashboardData.milestones.next,
              total: dashboardData.milestones.total_achieved,
            },
            chartData: newChartData,
            chartChange: selectedChartData?.change || 0,
            chartChangePercent: selectedChartData?.change_percent || 0,
            lastUpdated: new Date(),
            isNewUser: dashboardData.is_new_user,
            unreadNotifications: dashboardData.notifications.unread_count,
            isRefreshing: false,
            isLoading: false,
            error: null,
          };
        });
      } else {
        setState(prev => ({
          ...prev,
          isRefreshing: false,
          isLoading: false,
          error: 'Failed to load vault data',
        }));
      }
    } catch (error) {
      console.error('Refresh failed:', error);
      setState(prev => ({
        ...prev,
        isRefreshing: false,
        isLoading: false,
        error: 'Failed to refresh data',
      }));
    }
  }, [fetchDashboard, fetchChart, state.selectedTimeframe, checkForCelebrations]);

  // Calculate change values from chart data points
  const calculateChangeFromData = useCallback((data: VaultChartDataPoint[]): { change: number; changePercent: number } => {
    if (!data || data.length < 2) {
      return { change: 0, changePercent: 0 };
    }
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    const change = lastValue - firstValue;
    const changePercent = firstValue !== 0 ? (change / firstValue) * 100 : 0;
    return { change, changePercent };
  }, []);

  // Set timeframe and fetch data if needed
  const setTimeframe = useCallback(async (timeframe: VaultTimeframe) => {
    setState(prev => ({ ...prev, selectedTimeframe: timeframe, isChartLoading: true }));

    // Check if we already have data for this timeframe
    const existingData = state.chartData.get(timeframe);

    if (!existingData || existingData.length === 0) {
      const chartData = await fetchChart(timeframe);

      setState(prev => {
        const newChartData = new Map(prev.chartData);
        if (chartData) {
          newChartData.set(timeframe, chartData.data);
        }

        return {
          ...prev,
          chartData: newChartData,
          chartChange: chartData?.change || 0,
          chartChangePercent: chartData?.change_percent || 0,
          isChartLoading: false,
        };
      });
    } else {
      // Use cached data but still update chartChange and chartChangePercent
      const { change, changePercent } = calculateChangeFromData(existingData);
      setState(prev => ({
        ...prev,
        chartChange: change,
        chartChangePercent: changePercent,
        isChartLoading: false,
      }));
    }
  }, [fetchChart, state.chartData, calculateChangeFromData]);

  // Scrubbing handlers
  const startScrub = useCallback(() => {
    setState(prev => ({ ...prev, isScrubbing: true }));
  }, []);

  const updateScrub = useCallback((index: number) => {
    const chartData = state.chartData.get(state.selectedTimeframe);
    if (!chartData || index < 0 || index >= chartData.length) return;

    const point = chartData[index];
    setState(prev => ({
      ...prev,
      scrubIndex: index,
      scrubValue: point.value,
      scrubTimestamp: point.timestamp,
      displayValue: point.value,
    }));
  }, [state.chartData, state.selectedTimeframe]);

  const endScrub = useCallback(() => {
    setState(prev => ({
      ...prev,
      isScrubbing: false,
      scrubIndex: null,
      scrubValue: null,
      scrubTimestamp: null,
      displayValue: prev.currentValue,
    }));
  }, []);

  // Animate value with counting effect
  const animateValueTo = useCallback((targetValue: number) => {
    const startValue = state.displayValue;
    const startTime = performance.now();
    const duration = 1500; // 1.5 seconds

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic)
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + (targetValue - startValue) * easeProgress;

      setState(prev => ({
        ...prev,
        displayValue: currentValue,
      }));

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(animate);
  }, [state.displayValue]);

  // Process celebration queue
  useEffect(() => {
    if (!state.activeCelebration && state.pendingCelebrations.length > 0) {
      const nextCelebration = state.pendingCelebrations.find(c => !c.dismissed);
      if (nextCelebration) {
        setState(prev => ({
          ...prev,
          activeCelebration: nextCelebration,
        }));
      }
    }
  }, [state.pendingCelebrations, state.activeCelebration]);

  // Initial load
  useEffect(() => {
    refresh();
  }, []);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const value: VaultContextType = {
    ...state,
    refresh,
    setTimeframe,
    startScrub,
    updateScrub,
    endScrub,
    dismissCelebration,
    triggerCelebration,
    animateValueTo,
  };

  return (
    <VaultContext.Provider value={value}>
      {children}
    </VaultContext.Provider>
  );
};

export default VaultContext;
